/**
 * The Bible versions a player can read the game in.
 *
 * WEB's verse grid (data/build/bible.json, the `verses` table) is the one structure the whole game is built on: sections,
 * Round 3 slots, grading. Every other version only supplies the TEXT for each of WEB's verse cells, so switching version
 * can never move where a verse lives or invalidate a guess.
 */
export const TRANSLATION_CODES = ["WEB", "KJV", "NASB1995", "NIV"] as const;
export type TranslationCode = (typeof TRANSLATION_CODES)[number];
export const DEFAULT_TRANSLATION: TranslationCode = "WEB";

export interface TranslationInfo {
  code: TranslationCode;
  /** Short name for buttons and menus. */
  label: string;
  name: string;
  /**
   * The credit shown wherever this text appears. For the licensed versions this is the commonly published form of the
   * publisher's notice, which the maintainer approved using as-is on 2026-10-11. If a licence agreement ever asks for
   * different wording, change it here.
   */
  notice: string;
}

export const TRANSLATIONS: Readonly<Record<TranslationCode, TranslationInfo>> = {
  WEB: { code: "WEB", label: "WEB", name: "World English Bible", notice: "Scripture: World English Bible, public domain." },
  KJV: { code: "KJV", label: "KJV", name: "King James Version", notice: "Scripture: King James Version (Authorized Version), public domain in the United States." },
  NASB1995: {
    code: "NASB1995",
    label: "NASB 1995",
    name: "New American Standard Bible (1995)",
    notice: "Scripture quotations taken from the New American Standard Bible® (NASB), Copyright © 1960, 1971, 1977, 1995 by The Lockman Foundation. Used by permission. All rights reserved. www.lockman.org",
  },
  NIV: {
    code: "NIV",
    label: "NIV",
    name: "New International Version",
    notice: "Scripture quotations taken from The Holy Bible, New International Version® NIV®, Copyright © 1973, 1978, 1984, 2011 by Biblica, Inc.® Used by permission. All rights reserved worldwide.",
  },
};

export const isTranslationCode = (v: unknown): v is TranslationCode => typeof v === "string" && (TRANSLATION_CODES as readonly string[]).includes(v);

/**
 * Shown in place of a verse the chosen version doesn't have (some newer versions leave out verses the older manuscripts
 * carry). The slot stays, so Round 3 is identical in every version.
 */
export const missingVerseText = (code: TranslationCode): string => `[This verse is not in the ${TRANSLATIONS[code].label}.]`;
